import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useRegisterMutation } from '../../queries/auth';
import ReCaptcha from '../../components/ReCaptcha';
import AuthShell from '../../components/auth/AuthShell';
import BusinessTypeChips from '../../components/auth/BusinessTypeChips';
import { Banner, ErrorBanner, Field, PasswordField, Head, Submit, focusFirstInvalid } from '../../components/auth/parts';
import { classify, firstError, isTakenMessage, isDisposableMessage } from '../../components/auth/authErrors';
import { emailOk, nameOk, MIN_PASSWORD } from '../../components/auth/validators';
import useCountdown from '../../components/auth/useCountdown';
import useAuthTitle from '../../components/auth/useAuthTitle';

const Signup = () => {
  const { t, i18n } = useTranslation();
  useAuthTitle('signup');
  const a = (k) => t(`authPage.${k}`);
  const pair = (k) => t(`authPage.signup.${k}`, { returnObjects: true });

  const [v, setV] = useState({
    first: '', last: '', company: '', email: '', password: '', password2: '', cat: '', catother: '',
  });
  const [errs, setErrs] = useState({});
  const [banner, setBanner] = useState(null); // taken | disposable | rate | offline | server
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaErr, setCaptchaErr] = useState('');
  const [resetSignal, setResetSignal] = useState(0);
  const formRef = useRef(null);
  const cd = useCountdown();
  const register = useRegisterMutation({ inline: true });

  const set = (k) => (e) => {
    const val = e.target.value;
    setV((x) => ({ ...x, [k]: val }));
    setErrs((x) => ({ ...x, [k]: '' }));
    if (k === 'email' && (banner === 'taken' || banner === 'disposable')) setBanner(null);
  };

  const locked = banner === 'rate' && cd.left > 0;

  const validate = () => {
    const n = {};
    if (!v.first.trim()) n.first = a('eReq'); else if (!nameOk(v.first)) n.first = a('eName');
    if (!v.last.trim()) n.last = a('eReq'); else if (!nameOk(v.last)) n.last = a('eName');
    if (!v.company.trim()) n.company = a('eReq');
    if (!v.cat) n.cat = t('authPage.signup.eCat');
    else if (v.cat === 'other' && !v.catother.trim()) n.catother = a('eReq');
    if (!v.email.trim()) n.email = a('eReq'); else if (!emailOk(v.email)) n.email = a('eEmail');
    if (!v.password) n.password = a('eReq'); else if (v.password.length < MIN_PASSWORD) n.password = a('eShort');
    if (!v.password2) n.password2 = a('eReq'); else if (v.password2 !== v.password) n.password2 = a('eMatch');
    return n;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (locked || register.isLoading) return;
    const n = validate();
    // The captcha is a UX shortcut only — the backend is the control.
    const needCaptcha = !captchaToken;
    setErrs(n);
    setCaptchaErr(needCaptcha ? t('auth.captcha_required') : '');
    if (Object.keys(n).length) {
      if (n.cat) document.querySelector('input[name="cat"]')?.focus(); else focusFirstInvalid(formRef.current);
      return;
    }
    if (needCaptcha) return;

    setBanner(null);
    // Seed the new company's wall-clock timezone from the browser's IANA zone
    // (see the long comment this replaced in git history: companies created
    // without one silently inherited the Asia/Riyadh fallback).
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    const payload = {
      first_name: v.first.trim(),
      last_name: v.last.trim(),
      company_name: v.company.trim(),
      email: v.email.trim(),
      password: v.password,
      password_confirmation: v.password2,
      business_category: v.cat,
      timezone,
      // Field name is Google's, and it is what UserRequest validates.
      'g-recaptcha-response': captchaToken,
    };
    if (v.cat === 'other') payload.business_category_other = v.catother.trim();

    try {
      await register.mutateAsync(payload);
    } catch (err) {
      setResetSignal((x) => x + 1); // a solved token is single-use
      const info = classify(err);
      const E = info.errors;
      if (info.kind === 'rate') { cd.start(info.retryAfter); setBanner('rate'); return; }
      if (info.kind === 'offline') { setBanner('offline'); return; }
      if (info.kind === 'validation' || info.kind === 'client' || info.status === 503) {
        const next = {};
        const emailMsg = firstError(E, 'email');
        let b = null;
        if (info.code === 'email_undeliverable') next.email = a('eUndeliverable');
        else if (emailMsg) {
          if (isDisposableMessage(emailMsg)) b = 'disposable';
          else if (isTakenMessage(emailMsg)) b = 'taken';
          else next.email = a('eEmail');
        }
        const nameMsg = firstError(E, 'name', 'company_name');
        if (nameMsg) next.company = isTakenMessage(nameMsg) ? a('eCompanyTaken') : a('eReq');
        if (firstError(E, 'first_name')) next.first = a('eName');
        if (firstError(E, 'last_name')) next.last = a('eName');
        const pwMsg = firstError(E, 'password');
        if (pwMsg) next.password = /confirm/i.test(pwMsg) ? null : a('eShort');
        if (/confirm/i.test(pwMsg)) next.password2 = a('eMatch');
        if (firstError(E, 'business_category')) next.cat = t('authPage.signup.eCat');
        if (firstError(E, 'business_category_other')) next.catother = a('eReq');
        if (firstError(E, 'g-recaptcha-response') || info.status === 503) setCaptchaErr(a('eCaptcha'));
        const known = b || Object.values(next).some(Boolean) || firstError(E, 'g-recaptcha-response') || info.status === 503;
        if (b) setBanner(b);
        if (!known) { setBanner('server'); return; }
        setErrs(next);
        return;
      }
      setBanner('server');
    }
  };

  const loginLink = <> <Link to="/login">{t('authPage.login.btn')}</Link></>;

  return (
    <AuthShell>
      <Head title={t('authPage.signup.h')} text={t('authPage.signup.p')} />
      <form ref={formRef} noValidate onSubmit={onSubmit}>
        {banner === 'taken' && <Banner kind="crit" icon="alert" pair={pair('taken')} extra={loginLink} />}
        {banner === 'disposable' && <Banner kind="warn" icon="alert" pair={pair('disposable')} />}
        {(banner === 'rate' || banner === 'offline' || banner === 'server') && <ErrorBanner kind={banner} left={cd.left} />}

        <div className="ppa-row2">
          <Field id="first" label={t('authPage.signup.first')} autoComplete="given-name" value={v.first} onChange={set('first')} error={errs.first} />
          <Field id="last" label={t('authPage.signup.last')} autoComplete="family-name" value={v.last} onChange={set('last')} error={errs.last} />
        </div>
        <Field
          id="company" label={t('authPage.signup.company')} autoComplete="organization"
          placeholder={t('authPage.signup.companyPh')} value={v.company} onChange={set('company')} error={errs.company}
        />
        <BusinessTypeChips
          value={v.cat}
          onChange={(k) => { setV((x) => ({ ...x, cat: k })); setErrs((x) => ({ ...x, cat: '' })); }}
          other={v.catother} onOther={(val) => { setV((x) => ({ ...x, catother: val })); setErrs((x) => ({ ...x, catother: '' })); }}
          error={errs.cat} otherError={errs.catother}
        />
        <Field
          id="email" label={a('email')} type="email" ltr autoComplete="email" placeholder="name@cafe.sa"
          value={v.email} onChange={set('email')} error={errs.email} invalid={banner === 'taken' || banner === 'disposable'}
        />
        <PasswordField id="password" label={a('pw')} autoComplete="new-password" value={v.password} onChange={set('password')} error={errs.password} checklist />
        <PasswordField id="password2" label={a('pw2')} autoComplete="new-password" value={v.password2} onChange={set('password2')} error={errs.password2} />

        {/* reCAPTCHA v2 checkbox. A solved token is single-use, so the widget is
            reset on every failed submit (resetSignal) — otherwise a retry replays
            a spent token and Google answers `timeout-or-duplicate`. */}
        <div className="ppa-wrap">
          <div className="ppa-captcha-slot">
            <ReCaptcha
              hl={i18n.language}
              resetSignal={resetSignal}
              onChange={(token) => { setCaptchaToken(token); if (token) setCaptchaErr(''); }}
            />
          </div>
          {captchaErr && <span className="ppa-err" role="alert" style={{ marginTop: 6 }}>{captchaErr}</span>}
        </div>

        <Submit busy={register.isLoading} busyLabel={t('authPage.signup.busy')} disabled={locked}>{t('authPage.signup.btn')}</Submit>
      </form>
      <p className="ppa-alt">{t('authPage.signup.alt')} <Link to="/login">{t('authPage.signup.altA')}</Link></p>
    </AuthShell>
  );
};

export default Signup;
